import { type AvailabilityListing } from '@shared/types/availability';
import { type CancellationPolicy } from '@shared/types/reservations';
import { type RestaurantStatus } from '@shared/types/restaurants';
import { type Shift } from '@shared/types/shifts';
import { api } from '../api';

export interface RestaurantDetails {
  address: string;
  cancellationPolicy: CancellationPolicy | null;
  city: string;
  description: string;
  id: string;
  locationSlug: string;
  maxReservationGuests: number;
  name: string;
  nameSlug: string;
  phone: string;
  smsPhoneNumber: string | null;
  postalCode: string;
  schedule: number;
  shifts: Pick<
    Shift,
    'id' | 'name' | 'startDate' | 'endDate' | 'startTime' | 'endTime' | 'repeat'
  >[];
  state: string;
  status: RestaurantStatus;
  street: string;
  supportEmail: string | null;
  timezone: string;
  website: string;
  cuisine: string | null;
  dining: string | null;
  dressCode: string | null;
  dressCodeDescription: string | null;
  dressCodeRequired: boolean;
  showGoogleRating: boolean;
  /**
   * Optional: only present once the backend exposes it on the guest-facing
   * restaurant DTO. Consumers must treat this as always-possibly-undefined
   * and fall back to an address-based lookup when absent.
   */
  googlePlaceId?: string | null;
}

export interface Restaurant {
  address: string;
  city: string;
  description: string;
  id: string;
  locationSlug: string;
  maxReservationGuests: 0;
  name: string;
  nameSlug: string;
  phone: string;
  postalCode: string;
  schedule: number;
  state: string;
  status: string;
  street: string;
  supportEmail: string | null;
  timezone: string;
  website: string;
}

export interface Availability {
  isBiddable: boolean;
  isBuyable: boolean;
  isResale: boolean;
  guestCount: number;
  purchasePrice: number | null;
  /** Restaurant's standard price (cents); only set when purchasePrice is an ML price. */
  standardPrice?: number | null;
  isBestAvailable: boolean;
  isFeaturedUpgrade: boolean;
  listing: AvailabilityListing;
  serviceWindow: string;
  time: string;
  date: string;
}

export interface GuestVipTier {
  id: string;
  name: string;
  discountPercentage: number;
}

export interface GetGuestVipTierResponse {
  guestVipTier: GuestVipTier | null;
}

export interface RestaurantBulletin {
  id: string;
  name: string;
  description: string;
  closureId: string | null;
  endDate: string | null;
  repeat: string[];
  startDate: string;
}

export interface Closure {
  date: string;
  id: string;
  restaurantId: string;
}

export interface FloorPlanTableAvailability {
  startTime: string;
  endTime: string;
  minGuest: number;
  maxGuest: number;
}

export type FloorPlanTableAvailabilitiesResponse = Record<
  string, // '2024-10-23'
  FloorPlanTableAvailability[]
>;

export const getRestaurants = async (): Promise<Restaurant[]> =>
  api.get(`/restaurants`);

export const getRestaurantDetailsBySlug = async (
  locationSlug: string,
  nameSlug: string,
): Promise<RestaurantDetails> =>
  api.get(`/restaurants/${locationSlug}/${nameSlug}`);

export const getRestaurantDetailsById = async (
  restaurantId: string,
): Promise<RestaurantDetails> => api.get(`/restaurants/${restaurantId}`);

export const getAvailabilities = async ({
  restaurantId,
  date,
  guestCount,
  changingReservationId,
}: {
  restaurantId: string;
  date: string;
  guestCount: number;
  changingReservationId?: string;
}): Promise<Availability[]> => {
  const queryParams = new URLSearchParams({
    guestCount: guestCount.toString(),
    ...(changingReservationId ? { changingReservationId } : {}),
  });
  return api.get(
    `/restaurants/${restaurantId}/availabilities/${date}?${queryParams.toString()}`,
  );
};

export const getGuestVipTier = async (
  restaurantId: string,
): Promise<GetGuestVipTierResponse> =>
  api.get(`/restaurants/${restaurantId}/guest-vip-tier`);

export const getBulletinsForRestaurantIdAndDate = async (
  restaurantId: string,
  date: string,
  signal?: AbortSignal,
): Promise<RestaurantBulletin[]> =>
  api.get(`/restaurants/${restaurantId}/bulletins/${date}`, { signal });

export const getFloorPlanTableAvailabilities = async (
  restaurantId: string,
  floorPlanTableId: string,
  date: string,
): Promise<FloorPlanTableAvailabilitiesResponse> =>
  api.get(
    `/restaurants/${restaurantId}/floor-plan-tables/${floorPlanTableId}/availabilities?date=${date}`,
  );
